import React from "react";

// Purely presentational — the caller (Dashboard.jsx / NewDashboard1.jsx)
// shapes each row and passes already-resolved status colors (reusing
// whatever getStatusColors/getStatusName each dashboard already has), so
// this component never invents its own status-color logic. Reused for both
// "Today's Schedule" (no dateLabel) and "Upcoming Bookings" (dateLabel set).
//
// rows: [{ id, name, subtitle, providers?, timeLabel, dateLabel?, statusLabel, statusColors: {bgColor,borderColor,textColor}, onClick }]
const TodaysScheduleList = ({ title, viewAllLabel, onViewAll, rows = [], emptyLabel = "No bookings" }) => {
  const hasRows = rows.length > 0;
  return (
    <div
      style={{
        borderRadius: "14px",
        border: "1px solid #E3EBF8",
        background: "#fff",
        padding: "16px",
        // Today's Schedule and Upcoming sit side by side but usually have a
        // very different number of rows (e.g. 1 booking today vs 5 coming
        // up this week). Forcing this card to stretch and match the taller
        // sibling left a big dead block of empty space below a short list —
        // sizing to its own content instead means a short list makes a
        // short card, and two lists that both happen to be long naturally
        // end up close in height anyway (both capped at 360px below).
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <div style={{ fontSize: "15px", fontWeight: 600, color: "#111827" }}>{title}</div>
        {onViewAll && (
          <span
            onClick={onViewAll}
            style={{ fontSize: "12.5px", fontWeight: 600, color: "#d01c4c", cursor: "pointer" }}
          >
            {viewAllLabel}
          </span>
        )}
      </div>

      {!hasRows ? (
        <div style={{ padding: "12px 0", textAlign: "center", color: "#9CA3AF", fontSize: "13px" }}>
          {emptyLabel}
        </div>
      ) : (
        // Thin, styled scrollbar (see .dash-schedule-scroll in App.css) —
        // the default browser scrollbar is wide enough that once a list is
        // long enough to scroll, its rows visibly lose width/padding on
        // the right compared to a short, non-scrolling sibling list.
        <div className="dash-schedule-scroll" style={{ display: "flex", flexDirection: "column", maxHeight: "360px", overflowY: "auto" }}>
          {rows.map((r) => (
            <div
              key={r.id}
              onClick={r.onClick}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 4px",
                borderBottom: "1px solid #F3F4F6",
                cursor: r.onClick ? "pointer" : "default",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#E9ECEF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#495057",
                  flexShrink: 0,
                }}
              >
                {(r.name || "-").trim().charAt(0).toUpperCase()}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.name}
                </div>
                <div style={{ fontSize: "12px", color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.subtitle}
                </div>
                {r.providers && (
                  <div
                    title={r.providers}
                    style={{ fontSize: "11.5px", color: "#9CA3AF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {r.providers}
                  </div>
                )}
              </div>

              <div style={{ textAlign: "right", flexShrink: 0 }}>
                {/* Date + time on one line (not two) so a row with a
                    dateLabel (Upcoming) takes the same vertical space as a
                    row without one (Today's) — matches padding/row height
                    instead of Upcoming rows running taller. */}
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  {r.dateLabel ? `${r.dateLabel}, ${r.timeLabel}` : r.timeLabel}
                </div>
                {r.statusLabel && (
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: "2px",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      fontSize: "10.5px",
                      fontWeight: 600,
                      background: r.statusColors?.bgColor || "#F2F9FF",
                      color: r.statusColors?.textColor || "#037FE8",
                    }}
                  >
                    {r.statusLabel}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TodaysScheduleList;
